// Form state → request bodies for editing Events and occurrences. Pure
// functions (no React), so the request-shaping rules are unit-tested.
//
// Schedule conventions (same as iOS EventScheduleDraft / OccurrenceEditDraft):
// - Timed: browser-local datetime-local values, sent as instants.
// - All-day: inclusive calendar days in the EVENT's timezone, sent as the
//   first instants of the first day and of the day after the last day.
// - The schedule is sent only when the user changed it. An all-day schedule
//   counts as unchanged while its days are, so a content-only edit never
//   rewrites stored times (even ones saved before the midnight convention).

import { effectiveOccurrence, isRecurring } from "./eventDomain";
import {
  addDays,
  allDayDays,
  allDayInstants,
  daysBetween,
  fromDatetimeLocalValue,
  toDatetimeLocalValue,
} from "./eventTime";

export class DraftValidationError extends Error {}

const datePart = (localValue) => (localValue || "").slice(0, 10);

// ---------------------------------------------------------------------------
// Schedule (shared by the Event form and the occurrence form)
// ---------------------------------------------------------------------------

// { isAllDay, startsLocal, endsLocal, firstDay, lastDay } from stored
// instants. Timed values are always filled; days only for all-day.
export function scheduleFromInstants(startsAt, endsAt, isAllDay, timeZone) {
  const schedule = {
    isAllDay: Boolean(isAllDay),
    startsLocal: toDatetimeLocalValue(startsAt),
    endsLocal: toDatetimeLocalValue(endsAt),
    firstDay: "",
    lastDay: "",
  };
  if (schedule.isAllDay && startsAt && endsAt) {
    Object.assign(schedule, allDayDays(startsAt, endsAt, timeZone));
  }
  return schedule;
}

// Switches between all-day and timed. Switching back to the opening state
// restores the opening schedule; otherwise the day shown is kept (timed: a
// one-hour 9:00 slot, as the create form has always done).
export function toggleAllDay(schedule, allDay, original) {
  if (allDay === schedule.isAllDay) return schedule;
  if (original && allDay === original.isAllDay) return { ...original };
  if (allDay) {
    const day = datePart(schedule.startsLocal) || schedule.firstDay;
    return { ...schedule, isAllDay: true, firstDay: day, lastDay: day };
  }
  const day = schedule.firstDay || datePart(schedule.startsLocal);
  if (!day) return { ...schedule, isAllDay: false };
  return { ...schedule, isAllDay: false, startsLocal: `${day}T09:00`, endsLocal: `${day}T10:00` };
}

// Moving the first day moves the last day with it, keeping a multi-day
// all-day event's length.
export function setFirstDay(schedule, firstDay) {
  if (!firstDay) return { ...schedule, firstDay };
  if (!schedule.firstDay || !schedule.lastDay) return { ...schedule, firstDay, lastDay: firstDay };
  const length = Math.max(0, daysBetween(schedule.firstDay, schedule.lastDay));
  return { ...schedule, firstDay, lastDay: addDays(firstDay, length) };
}

// The instants the schedule describes. Throws DraftValidationError.
export function scheduleInstants(schedule, timeZone) {
  if (schedule.isAllDay) {
    if (!schedule.firstDay) throw new DraftValidationError("Choose a date.");
    const lastDay = schedule.lastDay || schedule.firstDay;
    if (lastDay < schedule.firstDay) {
      throw new DraftValidationError("The end date can't be before the start date.");
    }
    return allDayInstants(schedule.firstDay, lastDay, timeZone);
  }
  const starts_at = fromDatetimeLocalValue(schedule.startsLocal);
  const ends_at = fromDatetimeLocalValue(schedule.endsLocal);
  if (!starts_at || !ends_at) throw new DraftValidationError("Choose a start and end time.");
  if (new Date(ends_at) <= new Date(starts_at)) {
    throw new DraftValidationError("The end must be after the start.");
  }
  return { starts_at, ends_at };
}

export function scheduleChanged(schedule, original) {
  if (schedule.isAllDay !== original.isAllDay) return true;
  if (schedule.isAllDay) {
    return (
      schedule.firstDay !== original.firstDay ||
      (schedule.lastDay || schedule.firstDay) !== original.lastDay
    );
  }
  return schedule.startsLocal !== original.startsLocal || schedule.endsLocal !== original.endsLocal;
}

// ---------------------------------------------------------------------------
// Event form (create, one-off / former-series edit, Entire Series edit)
// ---------------------------------------------------------------------------

// The Event form's opening schedule. `occurrence` (Entire Series edits
// opened from a non-rescheduled occurrence) supplies the dates being looked
// at; the backend applies their wall-clock time to the series. All-day days
// are read in the event's own timezone.
export function initialEventSchedule(event, occurrence = null) {
  if (!event) {
    return { isAllDay: false, startsLocal: "", endsLocal: "", firstDay: "", lastDay: "" };
  }
  return scheduleFromInstants(
    occurrence?.starts_at ?? event.starts_at,
    occurrence?.ends_at ?? event.ends_at,
    event.is_all_day,
    event.timezone
  );
}

// Whether the edit (re)starts recurrence (backend is_recurrence_restart): a
// non-repeating Event made repeating, or an ending series whose end moves
// later or is removed.
export function restartsRecurrence(event, recurrence, recurrenceEndsAt) {
  if (!recurrence || recurrence === "none") return false;
  if (!isRecurring(event)) return true;
  if (!event.recurrence_ends_at) return false;
  return !recurrenceEndsAt || recurrenceEndsAt > event.recurrence_ends_at;
}

// PATCH /events/{id}/ body for an Event-form edit. Content and recurrence
// are always sent (the backend ignores unchanged values); starts_at/ends_at
// only when the user changed the schedule — any sent start re-anchors a
// series, and a resent all-day start computed in another timezone would
// shift the event.
export function buildEventUpdatePayload({
  event,
  form,
  schedule,
  originalSchedule,
  occurrence = null,
  backfill = false,
}) {
  const repeats = form.recurrence !== "none";
  const payload = {
    title: form.title,
    description: form.description,
    location: form.location,
    is_all_day: schedule.isAllDay,
    is_private: form.is_private,
    recurrence: form.recurrence,
    // Always sent (null when not repeating / no end) so clearing it sticks.
    recurrence_ends_at: repeats && form.recurrence_ends_at ? form.recurrence_ends_at : null,
  };
  if (scheduleChanged(schedule, originalSchedule)) {
    Object.assign(payload, scheduleInstants(schedule, event.timezone));
  }
  if (occurrence) payload.occurrence_id = occurrence.id;
  if (backfill) payload.backfill_past_occurrences = true;
  return payload;
}

// The 409 occurrence_deletion_confirmation_required text, built only from the
// counts the backend reported (iOS EditEventView.confirmationMessage).
export function seriesEditConfirmationMessage(impact, title, isRefresh) {
  const plural = (n, noun) => `${n} ${noun}${n === 1 ? "" : "s"}`;
  const removed = plural(impact.occurrencesToDelete, "upcoming occurrence");
  const parts = [
    isRefresh
      ? `The event changed or this confirmation expired. Saving will now remove ${removed}.`
      : `Saving this change will remove ${removed} of "${title}".`,
  ];
  if (impact.customizedPreserved > 0) {
    parts.push(`${plural(impact.customizedPreserved, "customized occurrence")} will be kept.`);
  }
  if (impact.scheduleOverridesRetained > 0) {
    parts.push(`${plural(impact.scheduleOverridesRetained, "rescheduled occurrence")} keep their own times.`);
  }
  if (impact.occurrencesToBackfill > 0) {
    parts.push(`${plural(impact.occurrencesToBackfill, "past occurrence")} will be restored.`);
  } else if (!isRefresh) {
    parts.push("Past occurrences aren't affected.");
  }
  return parts.join(" ");
}

// ---------------------------------------------------------------------------
// Occurrence form ("This Occurrence")
// ---------------------------------------------------------------------------

// One overridable content field: its value and whether it still inherits.
function contentField(seriesValue, override) {
  const original = override ?? null;
  return { series: seriesValue, original, value: original ?? seriesValue, inherits: original === null };
}

// Opening state, from the occurrence's effective values.
export function occurrenceDraft(event, occurrence) {
  const overrides = occurrence.overrides || {};
  const details = effectiveOccurrence({ event, occurrence });
  const schedule = scheduleFromInstants(
    occurrence.starts_at,
    occurrence.ends_at,
    details.is_all_day,
    event.timezone
  );
  return {
    title: contentField(event.title ?? "", overrides.title),
    description: contentField(event.description ?? "", overrides.description),
    location: contentField(event.location ?? "", overrides.location),
    is_private: contentField(Boolean(event.is_private), overrides.is_private),
    schedule,
    originalSchedule: schedule,
  };
}

// Typing into a field makes it this occurrence's own value.
export function setDraftField(draft, name, value) {
  if (value === draft[name].value) return draft;
  return { ...draft, [name]: { ...draft[name], value, inherits: false } };
}

// "Use series value": inherit the Event's value again.
export function resetDraftField(draft, name) {
  return { ...draft, [name]: { ...draft[name], value: draft[name].series, inherits: true } };
}

// What to send for one content field: undefined (omit) when unchanged, null
// to drop an override, or the new value as an override ("" included).
function contentPatchValue(field, normalize = (v) => v) {
  if (field.inherits) return field.original === null ? undefined : null;
  const initial = field.original ?? field.series;
  return normalize(field.value) === normalize(initial) ? undefined : normalize(field.value);
}

// PATCH /event-occurrences/{id}/ body: only what changed. Content-only edits
// carry no schedule fields, so they never detach the occurrence's schedule.
// Throws DraftValidationError.
export function buildOccurrencePatch(draft, timeZone) {
  const trim = (v) => v.trim();
  if (!draft.title.inherits && !draft.title.value.trim()) {
    throw new DraftValidationError("Add a title.");
  }
  const { schedule, originalSchedule } = draft;
  const instants = scheduleInstants(schedule, timeZone);

  const patch = {};
  const fields = {
    title: contentPatchValue(draft.title, trim),
    description: contentPatchValue(draft.description),
    location: contentPatchValue(draft.location),
    is_private: contentPatchValue(draft.is_private),
  };
  for (const [name, value] of Object.entries(fields)) {
    if (value !== undefined) patch[name] = value;
  }

  if (scheduleChanged(schedule, originalSchedule)) {
    Object.assign(patch, instants);
    if (schedule.isAllDay !== originalSchedule.isAllDay) patch.is_all_day = schedule.isAllDay;
  }
  return patch;
}
